/**
 * Pagina di presentazione (Andrea 2026-10-10): la prima cosa che vede chi
 * apre fantasquad.it senza essere entrato. Racconta in breve il fantacalcio
 * manageriale, con qualche grafica, e risponde alle domande piu' frequenti.
 * «Accedi» porta alla pagina di login; chi e' gia' entrato va dritto alla
 * Home della sua lega (la preferita o l'ultima usata).
 *
 * Testi: nomenclatura nostra (Pro, Bonus Difensivo, Squad Bet, Squad
 * Magazine), niente marchi altrui. Le FAQ stanno in `FAQ` qui sotto: per
 * aggiungerne una basta una riga (e la traduzione in en.json).
 */

import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { LogoApp } from "../common/LogoApp";
import { Maglia } from "../common/Maglia";
import { SelettoreLingua } from "../common/SelettoreLingua";
import { useAuthStore } from "../../lib/auth-store";
import { tr } from "../../i18n";

// ------------------------------------------------------------------
// Dati
// ------------------------------------------------------------------
const MAGLIE = [
  { pattern: "righe_verticali", coloreCorpo: "#2A0F4E", coloreDettaglio: "#D4AF37" },
  { pattern: "meta_verticale", coloreCorpo: "#B91C1C", coloreDettaglio: "#F8FAFC" },
  { pattern: "crociata", coloreCorpo: "#F8FAFC", coloreDettaglio: "#1D4ED8" },
  { pattern: "diagonale", coloreCorpo: "#0F766E", coloreDettaglio: "#FDE047" },
  { pattern: "scacchi", coloreCorpo: "#111827", coloreDettaglio: "#F97316" },
  { pattern: "v", coloreCorpo: "#7E22CE", coloreDettaglio: "#F8FAFC" },
];

const CICLO = [
  {
    n: "1",
    titolo: "Fondi il club",
    testo:
      "All'asta iniziale costruisci la rosa con il budget in fantamilioni. Dai un nome alla squadra e disegna la maglia.",
  },
  {
    n: "2",
    titolo: "Scendi in campo",
    testo:
      "Ogni giornata di Serie A schieri undici titolari, panchina, capitano e vice. I voti reali decidono il risultato.",
  },
  {
    n: "3",
    titolo: "Fai mercato",
    testo:
      "Nelle finestre estiva e invernale compri, vendi, scambi e presti giocatori agli altri presidenti, con proposte e controproposte.",
  },
  {
    n: "4",
    titolo: "Costruisci negli anni",
    testo:
      "Ogni estate decidi chi rinnovare. I giocatori restano tuoi: chi ha costruito bene raccoglie per stagioni.",
  },
];

const CARATTERISTICHE = [
  {
    icona: "📈",
    titolo: "Valori di mercato reali",
    testo:
      "Ogni giocatore vale quanto sul mercato vero, aggiornato in stagione. È la moneta del gioco: aste, rinnovi e compensazioni partono da lì.",
  },
  {
    icona: "🤝",
    titolo: "Un mercato vero",
    testo:
      "Otto tipi di operazione: acquisto, vendita, scambio, scambio con conguaglio e quattro tipi di prestito, anche con diritto o obbligo di riscatto.",
  },
  {
    icona: "🌱",
    titolo: "La leva dei giovani",
    testo:
      "Gli Under 23 non pagano il rinnovo: li tieni a costo zero mentre il loro valore cresce. È la mossa che fa vincere negli anni.",
  },
  {
    icona: "🧠",
    titolo: "Due modalità: Pro e Classic",
    testo:
      "Classic con i quattro ruoli di sempre, oppure Pro con ruoli precisi per ogni zona del campo e moduli più tattici. Più il Bonus Difensivo per chi ha una difesa solida.",
  },
  {
    icona: "🎲",
    titolo: "Squad Bet",
    testo:
      "Scommetti i tuoi fantamilioni sulle partite della lega e sfida gli altri presidenti. Un modo in più per far crescere il budget, o per perderlo.",
  },
  {
    icona: "📰",
    titolo: "Squad Magazine",
    testo:
      "La stampa della lega: prime pagine, report, dichiarazioni dei presidenti, la Top 11 e il manager della giornata.",
  },
  {
    icona: "👥",
    titolo: "Più leghe, un solo account",
    testo:
      "Crea la tua lega con gli amici o entra con un codice. Puoi giocare in più leghe e passare dall'una all'altra con un tocco.",
  },
  {
    icona: "📱",
    titolo: "Sempre con te",
    testo:
      "Funziona dal browser e si installa sul telefono come un'app. Puoi condividere formazione e risultato su WhatsApp e Telegram.",
  },
];

const NUMERI = [
  { valore: "8", etichetta: "tipi di operazione di mercato" },
  { valore: "15", etichetta: "moduli tattici" },
  { valore: "25", etichetta: "giocatori in lista" },
  { valore: "0M", etichetta: "rinnovo degli Under 23" },
];

const FAQ: { domanda: string; risposta: string }[] = [
  {
    domanda: "Cos'è il fantacalcio manageriale?",
    risposta:
      "È il fantacalcio in cui la squadra non si rifà da zero ogni anno. I giocatori restano tuoi finché non li vendi: li compri all'asta, li rinnovi ogni estate, li scambi e li presti agli altri presidenti. Vince chi costruisce meglio nel tempo, non chi azzecca una sola stagione.",
  },
  {
    domanda: "Come entro in una lega?",
    risposta:
      "Ti serve il codice della lega, che il presidente trova nella sua pagina «Le mie leghe», oppure un invito via email. Premi «Registrati», scegli «Unisciti con un codice» oppure apri il link dell'invito: entri subito con una squadra libera.",
  },
  {
    domanda: "Posso creare una lega mia con gli amici?",
    risposta:
      "Sì. In registrazione scegli «Crea una lega»: decidi nome, numero di squadre e modalità, poi condividi il codice con gli amici. Ne sarai il presidente. Se hai già un account, la trovi nel menu in alto alla voce «Crea o entra in una lega».",
  },
  {
    domanda: "Ho già un account: come entro?",
    risposta:
      "Premi «Accedi» e usa email e password. Se non ricordi la password, nella pagina di accesso c'è «Password dimenticata?»: ti arriva un'email per sceglierne una nuova.",
  },
  {
    domanda: "Non mi arriva l'email di invito o della password.",
    risposta:
      "Controlla spam e promozioni: le email partono da noreply@fantasquad.it. Se non c'è, chiedi al presidente della lega di rimandare l'invito, che genera un codice nuovo. Per la password puoi ripetere la richiesta dopo qualche minuto.",
  },
  {
    domanda: "Come metto FantaSquad sul telefono come un'app?",
    risposta:
      "Su iPhone apri fantasquad.it con Safari, tocca Condividi e poi «Aggiungi alla schermata Home». Su Android apri il sito con Chrome, tocca i tre puntini e poi «Installa app». L'icona compare tra le tue app e si apre a schermo intero.",
  },
  {
    domanda: "Entro quando devo schierare la formazione?",
    risposta:
      "Prima dell'inizio della giornata di Serie A: in Home trovi il conto alla rovescia. Passato il termine la formazione si blocca, e chi non l'ha consegnata prende il punteggio d'ufficio previsto dal regolamento della lega. Puoi salvare la stessa formazione per tutte le competizioni in un colpo.",
  },
  {
    domanda: "Che differenza c'è tra Pro e Classic?",
    risposta:
      "In Classic i ruoli sono quattro: portiere, difensore, centrocampista e attaccante. In Pro ogni giocatore ha ruoli più precisi, come terzino, mediano o trequartista, e ogni modulo chiede il ruolo giusto nella posizione giusta. La modalità si sceglie a inizio stagione.",
  },
  {
    domanda: "Come funzionano budget e valori dei giocatori?",
    risposta:
      "Ogni squadra ha un budget in fantamilioni che non può mai andare sotto zero. Ogni giocatore ha un valore di mercato reale: è la base delle aste, del rinnovo annuale, che costa una percentuale del valore, e delle compensazioni. Gli Under 23 non pagano il rinnovo.",
  },
  {
    domanda: "E se un mio giocatore lascia la Serie A?",
    risposta:
      "Scegli tu: tenerlo in rosa, non schierabile, rinnovandolo sul valore attuale, oppure incassare subito il suo valore come compensazione.",
  },
  {
    domanda: "Posso giocare in più leghe?",
    risposta:
      "Sì, con lo stesso account. Passi da una lega all'altra dal menu in alto, puoi ordinarle e segnarne una come preferita: è quella che si apre quando entri nell'app.",
  },
  {
    domanda: "Posso usare l'app in inglese?",
    risposta:
      "Sì: dal Profilo, alla voce Lingua, scegli English. Anche le email seguono la lingua scelta.",
  },
  {
    domanda: "Ho trovato un errore in rosa, nei prezzi o in un risultato.",
    risposta:
      "Scrivi al presidente della tua lega: dall'app può correggere rose, prezzi d'acquisto e risultati, con il motivo visibile a tutti. Se il problema è dell'app, lo segnala lui agli amministratori.",
  },
];

// ------------------------------------------------------------------
// Grafica: la "vetrina" del club nella copertina
// ------------------------------------------------------------------
// 4-3-3 su un campo 200x140 (la porta e' in basso)
const POSIZIONI: [number, number, boolean?][] = [
  [100, 128],
  [30, 104],
  [76, 108],
  [124, 108],
  [170, 104],
  [52, 76],
  [100, 82, true],
  [148, 76],
  [40, 40],
  [100, 30],
  [160, 40],
];

function Campo() {
  return (
    <svg viewBox="0 0 200 140" className="w-full h-auto" aria-hidden>
      <rect
        x="1"
        y="1"
        width="198"
        height="138"
        rx="8"
        className="fill-primary/25 stroke-accent/40"
      />
      <line x1="1" y1="70" x2="199" y2="70" className="stroke-accent/30" />
      <circle cx="100" cy="70" r="16" className="fill-none stroke-accent/30" />
      <rect x="60" y="1" width="80" height="22" className="fill-none stroke-accent/30" />
      <rect x="60" y="117" width="80" height="22" className="fill-none stroke-accent/30" />
      {POSIZIONI.map(([x, y, capitano], i) => (
        <g key={i}>
          <circle cx={x} cy={y} r="7" className={capitano ? "fill-accent" : "fill-fg/90"} />
          {capitano && (
            <text
              x={x}
              y={y + 3}
              textAnchor="middle"
              className="fill-ink"
              style={{ fontSize: 8, fontWeight: 800 }}
            >
              C
            </text>
          )}
        </g>
      ))}
    </svg>
  );
}

function Chip({ children, className }: { children: React.ReactNode; className: string }) {
  return (
    <div
      className={`absolute z-10 px-3 py-1.5 rounded-full text-[11px] font-bold whitespace-nowrap shadow-hero border border-accent/40 bg-bg-card/95 backdrop-blur fs-galleggia ${className}`}
    >
      {children}
    </div>
  );
}

function VetrinaClub() {
  return (
    <div className="relative mx-auto w-full max-w-sm px-4 sm:px-0">
      <div className="absolute -inset-8 rounded-[3rem] bg-accent/20 blur-3xl" aria-hidden />
      <div className="relative rounded-[2rem] border border-accent/30 bg-bg-card shadow-hero p-4 space-y-3">
        <div className="flex items-center gap-3">
          <Maglia
            pattern="righe_verticali"
            coloreCorpo="#2A0F4E"
            coloreDettaglio="#D4AF37"
            size={48}
          />
          <div className="min-w-0">
            <div className="font-display text-lg text-fg leading-tight">{tr("Il tuo club")}</div>
            <div className="text-[11px] text-fg-muted">{tr("Stagione 3 · Giornata 12")}</div>
          </div>
          <div className="ml-auto text-right">
            <div className="text-[10px] uppercase tracking-wider text-fg-muted">
              {tr("Classifica")}
            </div>
            <div className="font-display text-xl text-accent">2°</div>
          </div>
        </div>
        <div className="grid grid-cols-2 gap-2 text-center">
          <div className="rounded-xl bg-primary-deep/60 p-2">
            <div className="text-[10px] uppercase tracking-wider text-fg-muted">{tr("Budget")}</div>
            <div className="font-display text-lg text-fg">214M</div>
          </div>
          <div className="rounded-xl bg-primary-deep/60 p-2">
            <div className="text-[10px] uppercase tracking-wider text-fg-muted">
              {tr("Valore rosa")}
            </div>
            <div className="font-display text-lg text-fg">
              812M <span className="text-xs text-success">▲ 6%</span>
            </div>
          </div>
        </div>
        <Campo />
        <div className="flex items-center justify-between rounded-xl bg-accent/15 border border-accent/30 px-3 py-2">
          <div className="text-xs text-fg">
            <span className="text-fg-muted">{tr("Prossima sfida")} · </span>
            <b>{tr("vs Leoni FC")}</b>
          </div>
          <div className="font-mono text-xs text-accent">2g 14h 08m</div>
        </div>
      </div>
      <Chip className="-top-3 left-0 sm:-left-8">🌱 {tr("U23 · rinnovo 0M")}</Chip>
      <Chip className="top-48 -right-2 sm:-right-12 [animation-delay:1.2s]">
        📈 {tr("+4,5M di valore")}
      </Chip>
      <Chip className="bottom-24 -left-2 sm:-left-12 [animation-delay:2.1s]">
        🅒 {tr("Capitano +2")}
      </Chip>
      <Chip className="-bottom-3 right-2 sm:-right-6 [animation-delay:0.6s]">
        🤝 {tr("Scambio accettato")}
      </Chip>
    </div>
  );
}

// ------------------------------------------------------------------
// Pagina
// ------------------------------------------------------------------
function DomandaFaq({ domanda, risposta }: { domanda: string; risposta: string }) {
  return (
    <details className="group rounded-card border border-primary-soft/30 bg-bg-card open:border-accent/50 transition">
      <summary className="flex items-center justify-between gap-3 cursor-pointer list-none px-4 py-3 font-bold text-fg">
        <span>{tr(domanda)}</span>
        <span
          className="shrink-0 w-6 h-6 grid place-items-center rounded-full bg-accent/15 text-accent transition-transform group-open:rotate-45"
          aria-hidden
        >
          +
        </span>
      </summary>
      <p className="px-4 pb-4 text-sm leading-relaxed text-fg-muted">{tr(risposta)}</p>
    </details>
  );
}

export function LandingPage() {
  const loggato = useAuthStore((s) => !!s.accessToken);
  const [scorso, setScorso] = useState(false);

  useEffect(() => {
    const su = () => setScorso(window.scrollY > 12);
    su();
    window.addEventListener("scroll", su, { passive: true });
    return () => window.removeEventListener("scroll", su);
  }, []);

  const bottoneEntra = loggato ? (
    <Link to="/" className="btn-primary text-sm px-4 py-2">
      {tr("Vai alla tua lega")}
    </Link>
  ) : (
    <Link to="/login" className="btn-primary text-sm px-4 py-2">
      {tr("Accedi")}
    </Link>
  );

  return (
    <div className="min-h-screen bg-bg text-fg overflow-x-hidden">
      <style>{`
        @keyframes fs-galleggia { 0%,100% { transform: translateY(0) } 50% { transform: translateY(-6px) } }
        @media (prefers-reduced-motion: no-preference) {
          .fs-galleggia { animation: fs-galleggia 5s ease-in-out infinite; }
        }
        html { scroll-behavior: smooth; }
      `}</style>

      {/* Barra in alto */}
      <header
        className={`sticky top-0 z-30 transition ${
          scorso ? "bg-bg/85 backdrop-blur border-b border-primary-soft/30" : "bg-transparent"
        }`}
      >
        <div className="max-w-6xl mx-auto px-4 h-16 flex items-center gap-3">
          <Link to="/benvenuto" className="flex items-center gap-2 shrink-0">
            <LogoApp className="w-9 h-9" />
            <span className="font-display text-xl text-accent tracking-wide">FantaSquad</span>
          </Link>
          <nav className="hidden md:flex items-center gap-5 ml-6 text-sm text-fg-muted">
            <a href="#come-funziona" className="hover:text-fg">
              {tr("Come funziona")}
            </a>
            <a href="#caratteristiche" className="hover:text-fg">
              {tr("Caratteristiche")}
            </a>
            <a href="#faq" className="hover:text-fg">
              {tr("Domande frequenti")}
            </a>
          </nav>
          <div className="ml-auto flex items-center gap-2">
            <div className="hidden sm:block w-48">
              <SelettoreLingua compatto />
            </div>
            {bottoneEntra}
          </div>
        </div>
      </header>

      {/* Copertina */}
      <section className="relative">
        <div
          className="absolute inset-x-0 top-0 h-[36rem] bg-gradient-to-b from-primary/40 via-primary/10 to-transparent"
          aria-hidden
        />
        <div className="relative max-w-6xl mx-auto px-4 pt-10 pb-16 md:pt-16 md:pb-24 grid md:grid-cols-2 gap-12 items-center">
          <div className="space-y-6 text-center md:text-left">
            <div className="inline-block rounded-full border border-accent/40 bg-accent/10 px-3 py-1 text-[11px] font-bold uppercase tracking-[0.18em] text-accent">
              {tr("Il fantacalcio manageriale")}
            </div>
            <h1 className="font-display text-4xl sm:text-5xl lg:text-6xl leading-[1.05] text-fg">
              {tr("Il tuo club.")} <span className="text-accent">{tr("Per anni.")}</span>
            </h1>
            <p className="text-base sm:text-lg text-fg-muted max-w-xl mx-auto md:mx-0">
              {tr(
                "Su FantaSquad la squadra non si rifà da zero ogni estate. I giocatori restano tuoi: li compri, li rinnovi, li scambi e li presti come un vero direttore sportivo. Vince chi costruisce meglio nel tempo.",
              )}
            </p>
            <div className="flex flex-wrap gap-3 justify-center md:justify-start">
              {loggato ? (
                <Link to="/" className="btn-primary px-6 py-3 text-base">
                  {tr("Vai alla tua lega")}
                </Link>
              ) : (
                <>
                  <Link to="/login" className="btn-primary px-6 py-3 text-base">
                    {tr("Accedi")}
                  </Link>
                  <Link to="/register" className="btn-ghost px-6 py-3 text-base">
                    {tr("Registrati con un codice")}
                  </Link>
                </>
              )}
            </div>
            <a
              href="#come-funziona"
              className="inline-block text-sm text-fg-muted hover:text-accent"
            >
              {tr("Scopri come funziona ↓")}
            </a>
          </div>
          <VetrinaClub />
        </div>
      </section>

      {/* Numeri */}
      <section className="border-y border-primary-soft/30 bg-bg-card/60">
        <div className="max-w-6xl mx-auto px-4 py-6 grid grid-cols-2 md:grid-cols-4 gap-4 text-center">
          {NUMERI.map((n) => (
            <div key={n.etichetta}>
              <div className="font-display text-3xl text-accent">{n.valore}</div>
              <div className="text-xs text-fg-muted">{tr(n.etichetta)}</div>
            </div>
          ))}
        </div>
      </section>

      {/* Come funziona */}
      <section id="come-funziona" className="max-w-6xl mx-auto px-4 py-16 md:py-24 scroll-mt-16">
        <div className="text-center max-w-2xl mx-auto mb-12 space-y-3">
          <h2 className="font-display text-3xl sm:text-4xl text-fg">
            {tr("Il ciclo del tuo club")}
          </h2>
          <p className="text-fg-muted">
            {tr(
              "Una stagione su FantaSquad assomiglia a quella di un club vero: si fonda, si gioca, si tratta, si rinnova. E poi si ricomincia, più forti di prima.",
            )}
          </p>
        </div>
        <ol className="relative grid gap-6 md:grid-cols-4">
          <div
            className="hidden md:block absolute top-7 left-[12%] right-[12%] h-0.5 bg-gradient-to-r from-accent/0 via-accent/60 to-accent/0"
            aria-hidden
          />
          {CICLO.map((c) => (
            <li key={c.n} className="relative text-center space-y-3">
              <div className="mx-auto w-14 h-14 rounded-full grid place-items-center bg-accent text-ink font-display text-2xl shadow-hero">
                {c.n}
              </div>
              <h3 className="font-display text-xl text-fg">{tr(c.titolo)}</h3>
              <p className="text-sm text-fg-muted leading-relaxed">{tr(c.testo)}</p>
            </li>
          ))}
        </ol>
      </section>

      {/* Caratteristiche */}
      <section
        id="caratteristiche"
        className="bg-bg-card/50 border-y border-primary-soft/30 scroll-mt-16"
      >
        <div className="max-w-6xl mx-auto px-4 py-16 md:py-24">
          <div className="text-center max-w-2xl mx-auto mb-12 space-y-3">
            <h2 className="font-display text-3xl sm:text-4xl text-fg">
              {tr("Molto più di una formazione")}
            </h2>
            <p className="text-fg-muted">
              {tr("Tutto quello che serve per gestire un club, in un'app sola.")}
            </p>
          </div>
          <div className="grid gap-4 sm:grid-cols-2 lg:grid-cols-4">
            {CARATTERISTICHE.map((c) => (
              <div
                key={c.titolo}
                className="rounded-card border border-primary-soft/30 bg-bg p-5 space-y-2 hover:border-accent/50 hover:-translate-y-0.5 transition"
              >
                <div
                  className="w-11 h-11 rounded-xl grid place-items-center bg-accent/15 text-2xl"
                  aria-hidden
                >
                  {c.icona}
                </div>
                <h3 className="font-bold text-fg">{tr(c.titolo)}</h3>
                <p className="text-sm text-fg-muted leading-relaxed">{tr(c.testo)}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Maglie + competizioni */}
      <section className="max-w-6xl mx-auto px-4 py-16 md:py-24 grid gap-6 md:grid-cols-2">
        <div className="rounded-card border border-primary-soft/30 bg-bg-card p-6 space-y-4">
          <h2 className="font-display text-2xl text-fg">{tr("La maglia è tua")}</h2>
          <p className="text-sm text-fg-muted">
            {tr(
              "Scegli tra 15 modelli, i colori e il logo del club. I trofei vinti compaiono sulla maglia e in bacheca.",
            )}
          </p>
          <div className="grid grid-cols-3 gap-3 place-items-center pt-2">
            {MAGLIE.map((m) => (
              <div key={m.pattern} className="hover:-translate-y-1 transition">
                <Maglia {...m} size={84} />
              </div>
            ))}
          </div>
        </div>
        <div className="rounded-card border border-accent/40 bg-gradient-to-br from-primary/40 to-bg-card p-6 space-y-4 relative overflow-hidden">
          <img
            src="/squadcup-trofeo.webp"
            alt=""
            aria-hidden
            className="absolute -right-6 -bottom-6 w-44 opacity-90 pointer-events-none"
          />
          <h2 className="font-display text-2xl text-fg">{tr("Una stagione, tanti trofei")}</h2>
          <ul className="space-y-3 text-sm relative max-w-[70%]">
            <li>
              <b className="text-accent">🏆 {tr("Campionato")}</b>
              <div className="text-fg-muted">
                {tr("Tutti contro tutti, giornata dopo giornata.")}
              </div>
            </li>
            <li>
              <b className="text-accent">⭐ {tr("Coppa")}</b>
              <div className="text-fg-muted">
                {tr("Gironi e poi eliminazione diretta. Il nome lo scegli tu.")}
              </div>
            </li>
            <li>
              <b className="text-accent">👑 {tr("Supercoppa")}</b>
              <div className="text-fg-muted">
                {tr("Il campione contro il vincitore della coppa.")}
              </div>
            </li>
            <li>
              <b className="text-accent">🌍 SquadCup</b>
              <div className="text-fg-muted">
                {tr("I migliori club di tutte le leghe, uno contro l'altro.")}
              </div>
            </li>
          </ul>
        </div>
      </section>

      {/* FAQ */}
      <section id="faq" className="max-w-3xl mx-auto px-4 pb-16 md:pb-24 scroll-mt-16">
        <div className="text-center mb-10 space-y-3">
          <h2 className="font-display text-3xl sm:text-4xl text-fg">{tr("Domande frequenti")}</h2>
          <p className="text-fg-muted">{tr("Le risposte alle domande che ci fate più spesso.")}</p>
        </div>
        <div className="space-y-3">
          {FAQ.map((f) => (
            <DomandaFaq key={f.domanda} {...f} />
          ))}
        </div>
      </section>

      {/* Chiusura */}
      <section className="px-4 pb-16 md:pb-24">
        <div className="max-w-4xl mx-auto rounded-[2rem] border border-accent/40 bg-gradient-to-br from-primary/50 via-bg-card to-bg-card p-8 md:p-12 text-center space-y-5 shadow-hero">
          <h2 className="font-display text-3xl sm:text-4xl text-fg">
            {tr("Pronto a prendere in mano il tuo club?")}
          </h2>
          <p className="text-fg-muted max-w-xl mx-auto">
            {tr("Entra nella tua lega, oppure creane una e invita gli amici con un codice.")}
          </p>
          <div className="flex flex-wrap gap-3 justify-center">
            {loggato ? (
              <Link to="/" className="btn-primary px-6 py-3 text-base">
                {tr("Vai alla tua lega")}
              </Link>
            ) : (
              <>
                <Link to="/login" className="btn-primary px-6 py-3 text-base">
                  {tr("Accedi")}
                </Link>
                <Link to="/register" className="btn-ghost px-6 py-3 text-base">
                  {tr("Crea un account")}
                </Link>
              </>
            )}
          </div>
        </div>
      </section>

      <footer className="border-t border-primary-soft/30">
        <div className="max-w-6xl mx-auto px-4 py-6 flex flex-col sm:flex-row items-center justify-between gap-3 text-xs text-fg-muted">
          <div className="flex items-center gap-2">
            <LogoApp className="w-6 h-6" />
            <span>
              {tr("© {{anno}} FantaSquad · Tutti i diritti riservati", {
                anno: new Date().getFullYear(),
              })}
            </span>
          </div>
          <div className="sm:hidden w-48">
            <SelettoreLingua compatto />
          </div>
          <span>{tr("Il fantacalcio manageriale")}</span>
        </div>
      </footer>
    </div>
  );
}
